import React, { useState, useEffect } from 'react';
import { X, MessageSquare, User, Calendar } from 'lucide-react';
import { publicationAPI } from '../../services/api';

interface ResubmissionFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  publicationId: number;
  publicationTitle: string;
}

const ResubmissionFeedbackModal: React.FC<ResubmissionFeedbackModalProps> = ({
  isOpen,
  onClose,
  publicationId,
  publicationTitle
}) => {
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && publicationId) {
      loadFeedback();
    }
  }, [isOpen, publicationId]);

  const loadFeedback = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await publicationAPI.getResubmissionFeedback(publicationId);
      setFeedback(response.feedback);
    } catch (error: any) {
      console.error('Error loading resubmission feedback:', error);
      setError('Failed to load feedback');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <MessageSquare className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Resubmission Feedback</h2>
              <p className="text-sm text-gray-600">{publicationTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-600 border-t-transparent" />
              <span className="ml-3 text-gray-600">Loading feedback...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">{error}</div>
              <button
                onClick={loadFeedback}
                className="text-orange-600 hover:text-orange-700 text-sm"
              >
                Try again
              </button>
            </div>
          ) : !feedback ? (
            <div className="text-center py-12">
              <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Feedback Available</h3>
              <p className="text-gray-600">No resubmission feedback has been provided yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <MessageSquare className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-medium text-orange-900 mb-2">Feedback from Publication Committee</h4>
                    <div className="text-sm text-orange-800 whitespace-pre-wrap">
                      {feedback}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <User className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-2">Next Steps</h4>
                    <div className="text-sm text-blue-800 space-y-2">
                      <p>Please review the feedback above and make the necessary changes to your publication.</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Click the <strong>"Edit"</strong> button on the publication page</li>
                        <li>Make the requested changes based on the feedback</li>
                        <li>Click <strong>"Resubmit for Review"</strong> when ready</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResubmissionFeedbackModal;
